import { Avatar as UiAvatar } from '@Pages/pageRuntime/components/ui/Avatar';
import { AvatarFallback } from '@Pages/pageRuntime/components/ui/AvatarFallback';
import { AvatarImage } from '@Pages/pageRuntime/components/ui/AvatarImage';
import { cn } from '@Pages/pageRuntime/lib/utils';
import type React from 'react';

type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';

type AvatarProps = {
  /**
   * Image URL. The fallback is shown when this is missing.
   */
  src?: string | null;

  /**
   * Accessible image description.
   */
  alt?: string;

  /**
   * Fallback text or node when no image is available.
   */
  fallback?: React.ReactNode;

  /**
   * Avatar size. Defaults to `md`.
   */
  size?: AvatarSize;

  className?: string;
};

const avatarSizeClasses: Record<AvatarSize, string> = {
  sm: 'size-8',
  md: 'size-10',
  lg: 'size-14',
  xl: 'size-20'
};

/**
 * A circular image or fallback mark for people, organizations, and records. Always pass
 * useful fallback content when the source may be missing.
 *
 * @example
 * <Sdk.Components.Avatar
 *   src={Sdk.utils.file(student.avatar)}
 *   alt={student.name ?? 'Student avatar'}
 *   fallback={student.initials}
 * />
 */
export function Avatar({ src, alt = '', fallback = '?', size = 'md', className }: AvatarProps) {
  return (
    <UiAvatar className={cn(avatarSizeClasses[size], className)}>
      {src ? <AvatarImage src={src} alt={alt} /> : null}
      <AvatarFallback>{fallback}</AvatarFallback>
    </UiAvatar>
  );
}
